import React from 'react';

const TRUST_PILLARS = [
  {
    icon: '🔒',
    title: '100% Source Code Ownership',
    desc: 'You own everything we build. Full IP transfer with zero vendor lock-in, proprietary licensing fees, or hidden recurring costs.',
  },
  {
    icon: '☁️',
    title: 'Cloud-Native & FinOps Built-In',
    desc: 'Engineered for AWS, Azure, and GCP with automated cost optimization, containerization, and right-sizing built into the codebase.',
  },
  {
    icon: '🌐',
    title: 'Bilingual Team (EN / ES)',
    desc: 'Seamless bilingual communication and documentation for cross-border engineering teams across North America and Latin America.',
  },
  {
    icon: '🚀',
    title: 'Proven Production SaaS Execution',
    desc: 'We operate our own live SaaS products (GNL Interpreters), proving high-availability execution in healthcare and real-time WebRTC.',
  },
];

const CREDENTIALS = [
  { category: 'Cloud Architecture', title: 'Multi-Cloud Deployments (AWS / GCP / Azure)', detail: 'Serverless, Docker/K8s, FinOps Cost Optimization' },
  { category: 'Full-Stack Engineering', title: 'Modern Production Stack', detail: 'React, TypeScript, Python (FastAPI/Django), PostgreSQL' },
  { category: 'Quality & Reliability', title: 'Automated CI/CD & Testing', detail: 'Unit, Integration & E2E Testing, Zero-Downtime Releases' },
  { category: 'Real-Time Systems', title: 'High-Availability Media & Data', detail: 'WebRTC, WebSockets, Live Telemetry & Microservices' },
  { category: 'Security & Governance', title: 'Enterprise Data Protection', detail: 'Encrypted Data Storage, OWASP Standards, OAuth2/JWT' },
  { category: 'Global Delivery', title: 'Bilingual Technical Support (EN/ES)', detail: 'Full documentation, agile sprints, & cross-border support' },
];

const PRICING_TIERS = [
  {
    name: 'Automation & Cloud Audit',
    price: '$0',
    subtitle: 'Free Consultation',
    desc: 'Perfect for businesses seeking to identify process bottlenecks and cloud cost leaks.',
    features: [
      '45-Minute Process Bottleneck Mapping',
      'Cloud Infrastructure Cost Leak Audit',
      'Custom Software Strategy & Roadmap Report',
      'Fixed-Price Quote & Timeline Estimate',
    ],
    cta: 'Book Free Audit',
    popular: false,
  },
  {
    name: 'MVP Launchpad',
    price: 'Starting at $5,000',
    subtitle: 'Rapid Production Tool',
    desc: 'Ideal for small businesses needing a custom internal tool, portal, or automated workflow fast.',
    features: [
      'Production-Ready Web Application or Portal',
      'Custom Database & API Integration',
      'Automated CI/CD & Cloud Deployment',
      '100% Source Code Ownership',
      '30 Days Post-Launch Support',
    ],
    cta: 'Start MVP Project',
    popular: true,
  },
  {
    name: 'Custom SaaS Platform',
    price: 'Custom Quote',
    subtitle: 'Full-Scale Enterprise Software',
    desc: 'For organizations building complex SaaS products, real-time media systems, or multi-cloud infrastructure.',
    features: [
      'Enterprise Full-Stack Architecture (React/Python)',
      'Real-Time Systems (WebRTC / Sockets / Telemetry)',
      'HIPAA / SOC2 Compliance Engineering',
      'Dedicated DevOps & FinOps Automation',
      '24/7 SLA-Backed Support',
    ],
    cta: 'Schedule Consultation',
    popular: false,
  },
];

const TrustCredentials: React.FC = () => {
  return (
    <section id="why-us" className="py-24 bg-gray-50 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-3">
            Why CloudIgnite
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
            Built on Technical Rigor, Transparency, and <span className="text-blue-700">Proven Credentials</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We bridge the gap between enterprise software engineering standards and small business agility.
          </p>
        </div>

        {/* 4 Trust Pillars */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {TRUST_PILLARS.map((p) => (
            <div key={p.title} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <span className="text-3xl block mb-3">{p.icon}</span>
              <h3 className="font-bold text-gray-900 text-base mb-2">{p.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        {/* Engineering Standards & Architecture Banner */}
        <div className="bg-slate-900 text-white rounded-3xl p-8 lg:p-10 mb-20">
          <div className="max-w-2xl mb-8">
            <span className="px-3 py-1 bg-blue-500/20 text-blue-300 text-xs font-semibold rounded-full border border-blue-500/30 mb-3 inline-block">
              Engineering Standards & Architecture
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Production-Grade Standards & Cloud Mastery</h3>
            <p className="text-slate-300 text-sm">
              Our engineering methodology is built on multi-cloud compliance, modern serverless & containerized infrastructure, and battle-tested DevOps pipelines.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CREDENTIALS.map((c, i) => (
              <div key={i} className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-5">
                <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1">{c.category}</p>
                <h4 className="text-sm font-bold text-white mb-1.5">{c.title}</h4>
                <p className="text-xs text-slate-300 font-mono">{c.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Engagement Tiers & Pricing */}
        <div>
          <div className="max-w-3xl mb-12">
            <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-2">Engagement Models</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Clear, Transparent Scope & Pricing</h3>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`bg-white rounded-3xl p-8 border flex flex-col justify-between relative transition-all duration-200 ${
                  tier.popular
                    ? 'border-blue-600 shadow-2xl ring-2 ring-blue-600/20'
                    : 'border-gray-200 shadow-sm hover:shadow-md'
                }`}
              >
                {tier.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
                    Most Popular Choice
                  </span>
                )}

                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">{tier.name}</h4>
                  <p className="text-xs text-gray-500 font-medium mb-4">{tier.subtitle}</p>
                  <div className="text-3xl font-extrabold text-blue-700 mb-4">{tier.price}</div>
                  <p className="text-xs text-gray-600 leading-relaxed mb-6 border-b border-gray-100 pb-4">{tier.desc}</p>

                  <div className="space-y-2.5 mb-8">
                    {tier.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                        <span className="text-blue-600 font-bold">✓</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <a
                  href="#contact"
                  className={`w-full text-center py-3 rounded-xl font-semibold text-xs transition-colors ${
                    tier.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/30'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default TrustCredentials;
